import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  Shield,
  Plus,
  Trash2,
  Loader2,
  AlertCircle,
  CheckCircle2,
  XCircle,
  Clock,
  CreditCard,
  X,
} from 'lucide-react';
import { tradingApi } from '../../services/tradingApi';
import type { RbacUserDetail, RbacRoleDetail, RbacOverrideDetail, RbacAuditLogEntry } from '../../types/trading';
import toast from 'react-hot-toast';

type DetailTab = 'roles' | 'overrides' | 'audit';

const KNOWN_ROLES = ['free', 'pro', 'premium', 'admin', 'ea-client'];

function formatDate(iso: string | null): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleString();
}

// ---------------------------------------------------------------------------
// Grant Role Modal
// ---------------------------------------------------------------------------

function GrantRoleModal({
  userId,
  onClose,
  onSuccess,
}: {
  userId: number;
  onClose: () => void;
  onSuccess: () => void;
}) {
  const [roleName, setRoleName] = useState('');
  const [customRole, setCustomRole] = useState('');
  const [expiresAt, setExpiresAt] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const effectiveRole = roleName === '__custom__' ? customRole : roleName;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!effectiveRole.trim()) return;
    setLoading(true);
    setError(null);
    try {
      await tradingApi.grantRole(userId, effectiveRole.trim(), expiresAt || undefined);
      toast.success(`Role '${effectiveRole}' granted`);
      onSuccess();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to grant role');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
      <div className="bg-slate-800 border border-slate-700 rounded-xl w-full max-w-md shadow-2xl">
        <div className="px-5 py-4 border-b border-slate-700">
          <h3 className="text-white font-semibold">Grant Role</h3>
        </div>
        <form onSubmit={handleSubmit} className="p-5 space-y-4">
          {error && (
            <div className="flex items-center gap-2 p-3 bg-red-900/20 border border-red-800 rounded-lg text-red-400 text-sm">
              <AlertCircle className="h-4 w-4 flex-shrink-0" />
              {error}
            </div>
          )}

          <div>
            <label className="block text-sm text-slate-300 mb-1.5">Role</label>
            <select
              value={roleName}
              onChange={(e) => setRoleName(e.target.value)}
              required
              className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded-lg text-white text-sm focus:outline-none focus:ring-1 focus:ring-indigo-500"
            >
              <option value="">Select role…</option>
              {KNOWN_ROLES.map((r) => (
                <option key={r} value={r}>{r}</option>
              ))}
              <option value="__custom__">Custom…</option>
            </select>
          </div>

          {roleName === '__custom__' && (
            <div>
              <label className="block text-sm text-slate-300 mb-1.5">Custom role name</label>
              <input
                type="text"
                value={customRole}
                onChange={(e) => setCustomRole(e.target.value)}
                placeholder="e.g. partner"
                required
                className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded-lg text-white text-sm focus:outline-none focus:ring-1 focus:ring-indigo-500"
              />
            </div>
          )}

          <div>
            <label className="block text-sm text-slate-300 mb-1.5">
              Expires at <span className="text-slate-500">(optional)</span>
            </label>
            <input
              type="datetime-local"
              value={expiresAt}
              onChange={(e) => setExpiresAt(e.target.value)}
              className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded-lg text-white text-sm focus:outline-none focus:ring-1 focus:ring-indigo-500"
            />
          </div>

          <div className="flex gap-3 pt-1">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 border border-slate-600 text-slate-300 rounded-lg text-sm hover:bg-slate-700 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading || !effectiveRole.trim()}
              className="flex-1 px-4 py-2 bg-indigo-600 hover:bg-indigo-500 text-white rounded-lg text-sm font-medium disabled:opacity-50 transition-colors flex items-center justify-center gap-2"
            >
              {loading && <Loader2 className="h-4 w-4 animate-spin" />}
              Grant Role
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Add Override Modal
// ---------------------------------------------------------------------------

function AddOverrideModal({
  userId,
  onClose,
  onSuccess,
}: {
  userId: number;
  onClose: () => void;
  onSuccess: () => void;
}) {
  const [permissionKey, setPermissionKey] = useState('');
  const [granted, setGranted] = useState(true);
  const [reason, setReason] = useState('');
  const [expiresAt, setExpiresAt] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!permissionKey.trim() || !reason.trim()) return;
    setLoading(true);
    setError(null);
    try {
      await tradingApi.addPermissionOverride(
        userId,
        permissionKey.trim(),
        granted,
        reason.trim(),
        expiresAt || undefined,
      );
      const kind = granted ? 'GRANT' : 'REVOKE';
      toast.success(`${kind} override added for ${permissionKey}`);
      onSuccess();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to add override');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
      <div className="bg-slate-800 border border-slate-700 rounded-xl w-full max-w-md shadow-2xl">
        <div className="px-5 py-4 border-b border-slate-700">
          <h3 className="text-white font-semibold">Add Permission Override</h3>
        </div>
        <form onSubmit={handleSubmit} className="p-5 space-y-4">
          {error && (
            <div className="flex items-center gap-2 p-3 bg-red-900/20 border border-red-800 rounded-lg text-red-400 text-sm">
              <AlertCircle className="h-4 w-4 flex-shrink-0" />
              {error}
            </div>
          )}

          <div>
            <label className="block text-sm text-slate-300 mb-1.5">Permission key</label>
            <input
              type="text"
              value={permissionKey}
              onChange={(e) => setPermissionKey(e.target.value)}
              placeholder="e.g. feature:banker"
              required
              className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded-lg text-white text-sm focus:outline-none focus:ring-1 focus:ring-indigo-500"
            />
          </div>

          <div>
            <label className="block text-sm text-slate-300 mb-1.5">Type</label>
            <div className="flex gap-3">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  checked={granted}
                  onChange={() => setGranted(true)}
                  className="accent-green-500"
                />
                <span className="text-sm text-green-400">GRANT</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  checked={!granted}
                  onChange={() => setGranted(false)}
                  className="accent-red-500"
                />
                <span className="text-sm text-red-400">REVOKE</span>
              </label>
            </div>
          </div>

          <div>
            <label className="block text-sm text-slate-300 mb-1.5">Reason</label>
            <input
              type="text"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Why is this override needed?"
              required
              className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded-lg text-white text-sm focus:outline-none focus:ring-1 focus:ring-indigo-500"
            />
          </div>

          <div>
            <label className="block text-sm text-slate-300 mb-1.5">
              Expires at <span className="text-slate-500">(optional)</span>
            </label>
            <input
              type="datetime-local"
              value={expiresAt}
              onChange={(e) => setExpiresAt(e.target.value)}
              className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded-lg text-white text-sm focus:outline-none focus:ring-1 focus:ring-indigo-500"
            />
          </div>

          <div className="flex gap-3 pt-1">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 border border-slate-600 text-slate-300 rounded-lg text-sm hover:bg-slate-700 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading || !permissionKey.trim() || !reason.trim()}
              className="flex-1 px-4 py-2 bg-indigo-600 hover:bg-indigo-500 text-white rounded-lg text-sm font-medium disabled:opacity-50 transition-colors flex items-center justify-center gap-2"
            >
              {loading && <Loader2 className="h-4 w-4 animate-spin" />}
              Add Override
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Refund Modal
// ---------------------------------------------------------------------------

function RefundModal({
  userId,
  onClose,
  onSuccess,
}: {
  userId: number;
  onClose: () => void;
  onSuccess: () => void;
}) {
  const [amountDisplay, setAmountDisplay] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const amountCents = Math.round(parseFloat(amountDisplay) * 100);
    if (isNaN(amountCents) || amountCents <= 0) {
      setError('Enter a valid positive amount');
      return;
    }
    setLoading(true);
    setError(null);
    try {
      await tradingApi.processRefund(userId, amountCents);
      toast.success('Refund processed successfully');
      onSuccess();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Refund failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-slate-800 rounded-xl shadow-2xl max-w-sm w-full mx-4 p-6">
        <button onClick={onClose} className="absolute top-4 right-4 text-slate-400 hover:text-white">
          <X size={18} />
        </button>
        <div className="flex items-center gap-3 mb-4">
          <div className="p-2 bg-amber-900/30 rounded-lg">
            <CreditCard size={20} className="text-amber-400" />
          </div>
          <h3 className="text-white font-semibold">Process Refund</h3>
        </div>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm text-slate-400 mb-1">Amount (ZAR)</label>
            <input
              type="number"
              min="0.01"
              max="100000"
              step="0.01"
              value={amountDisplay}
              onChange={(e) => setAmountDisplay(e.target.value)}
              placeholder="e.g. 99.00"
              className="w-full bg-slate-700/50 border border-slate-600 rounded-lg px-3 py-2 text-white focus:outline-none focus:border-amber-500"
              required
            />
          </div>
          {error && <p className="text-sm text-red-400">{error}</p>}
          <div className="flex gap-3">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 bg-slate-700 hover:bg-slate-600 text-white rounded-lg transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading || !amountDisplay}
              className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-amber-600 hover:bg-amber-500 text-white font-semibold rounded-lg transition-colors disabled:opacity-50"
            >
              {loading ? <Loader2 size={16} className="animate-spin" /> : <CreditCard size={16} />}
              Refund
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Extend Trial Modal
// ---------------------------------------------------------------------------

function ExtendTrialModal({
  userId,
  onClose,
  onSuccess,
}: {
  userId: number;
  onClose: () => void;
  onSuccess: () => void;
}) {
  const [days, setDays] = useState(7);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (days < 1 || days > 90) {
      setError('Days must be between 1 and 90');
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const result = await tradingApi.extendTrial(userId, days);
      toast.success(`Trial extended — new expiry: ${new Date(result.new_expires_at).toLocaleDateString()}`);
      onSuccess();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to extend trial');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-slate-800 rounded-xl shadow-2xl max-w-sm w-full mx-4 p-6">
        <button onClick={onClose} className="absolute top-4 right-4 text-slate-400 hover:text-white">
          <X size={18} />
        </button>
        <div className="flex items-center gap-3 mb-4">
          <div className="p-2 bg-indigo-900/30 rounded-lg">
            <Clock size={20} className="text-indigo-400" />
          </div>
          <h3 className="text-white font-semibold">Extend Trial</h3>
        </div>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm text-slate-400 mb-1">Days to add (1–90)</label>
            <input
              type="number"
              min={1}
              max={90}
              value={days}
              onChange={(e) => setDays(parseInt(e.target.value, 10) || 1)}
              className="w-full bg-slate-700/50 border border-slate-600 rounded-lg px-3 py-2 text-white focus:outline-none focus:border-indigo-500"
              required
            />
          </div>
          {error && <p className="text-sm text-red-400">{error}</p>}
          <div className="flex gap-3">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 bg-slate-700 hover:bg-slate-600 text-white rounded-lg transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-indigo-600 hover:bg-indigo-500 text-white font-semibold rounded-lg transition-colors disabled:opacity-50"
            >
              {loading ? <Loader2 size={16} className="animate-spin" /> : <Clock size={16} />}
              Extend
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------

export default function RbacUserDetailPage() {
  const { userId } = useParams<{ userId: string }>();
  const navigate = useNavigate();

  const [user, setUser] = useState<RbacUserDetail | null>(null);
  const [auditEntries, setAuditEntries] = useState<RbacAuditLogEntry[]>([]);
  const [auditTotal, setAuditTotal] = useState(0);
  const [auditPage, setAuditPage] = useState(1);
  const [includeExpired, setIncludeExpired] = useState(false);
  const [tab, setTab] = useState<DetailTab>('roles');
  const [loading, setLoading] = useState(true);
  const [auditLoading, setAuditLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [grantRoleOpen, setGrantRoleOpen] = useState(false);
  const [addOverrideOpen, setAddOverrideOpen] = useState(false);
  const [refundOpen, setRefundOpen] = useState(false);
  const [extendTrialOpen, setExtendTrialOpen] = useState(false);

  const id = parseInt(userId ?? '0', 10);

  const loadUser = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await tradingApi.getRbacUserDetail(id, includeExpired);
      setUser(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load user');
    } finally {
      setLoading(false);
    }
  };

  const loadAudit = async (page = 1) => {
    setAuditLoading(true);
    try {
      const data = await tradingApi.getUserRbacAuditLog(id, { page, page_size: 20 });
      setAuditEntries(data.entries);
      setAuditTotal(data.total);
      setAuditPage(page);
    } catch {
      // non-fatal
    } finally {
      setAuditLoading(false);
    }
  };

  useEffect(() => {
    loadUser();
  }, [id, includeExpired]);

  useEffect(() => {
    if (tab === 'audit') {
      loadAudit(1);
    }
  }, [tab]);

  const handleRevokeRole = async (roleName: string) => {
    if (!confirm(`Revoke role '${roleName}' from ${user?.email}?`)) return;
    try {
      await tradingApi.revokeRole(id, roleName);
      toast.success(`Role '${roleName}' revoked`);
      loadUser();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to revoke role');
    }
  };

  const handleRemoveOverride = async (key: string) => {
    if (!confirm(`Remove override for '${key}'?`)) return;
    try {
      await tradingApi.removePermissionOverride(id, key);
      toast.success('Override removed');
      loadUser();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to remove override');
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-20">
        <Loader2 className="h-6 w-6 text-indigo-400 animate-spin" />
      </div>
    );
  }

  if (error || !user) {
    return (
      <div className="space-y-4">
        <button
          onClick={() => navigate('/admin/access-control/users')}
          className="flex items-center gap-2 text-slate-400 hover:text-white text-sm transition-colors"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to users
        </button>
        <div className="flex items-center gap-2 p-4 bg-red-900/20 border border-red-800 rounded-lg text-red-400">
          <AlertCircle className="h-4 w-4" />
          {error ?? 'User not found'}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Modals */}
      {grantRoleOpen && (
        <GrantRoleModal
          userId={id}
          onClose={() => setGrantRoleOpen(false)}
          onSuccess={() => { setGrantRoleOpen(false); loadUser(); }}
        />
      )}
      {addOverrideOpen && (
        <AddOverrideModal
          userId={id}
          onClose={() => setAddOverrideOpen(false)}
          onSuccess={() => { setAddOverrideOpen(false); loadUser(); }}
        />
      )}
      {refundOpen && (
        <RefundModal
          userId={id}
          onClose={() => setRefundOpen(false)}
          onSuccess={loadUser}
        />
      )}
      {extendTrialOpen && (
        <ExtendTrialModal
          userId={id}
          onClose={() => setExtendTrialOpen(false)}
          onSuccess={loadUser}
        />
      )}

      {/* Back nav */}
      <button
        onClick={() => navigate('/admin/access-control/users')}
        className="flex items-center gap-2 text-slate-400 hover:text-white text-sm transition-colors"
      >
        <ArrowLeft className="h-4 w-4" />
        Back to users
      </button>

      {/* User header */}
      <div className="bg-slate-800 border border-slate-700 rounded-xl p-5">
        <div className="flex items-start justify-between">
          <div>
            <h2 className="text-white font-semibold text-lg">{user.email}</h2>
            {user.full_name && <p className="text-slate-400 text-sm">{user.full_name}</p>}
            <div className="flex items-center gap-2 mt-2">
              <span className="px-2 py-0.5 bg-slate-700 text-slate-300 rounded-full text-xs">
                {user.subscription_tier}
              </span>
              <span className="text-slate-500 text-xs">ID #{user.id}</span>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setExtendTrialOpen(true)}
              className="flex items-center gap-2 px-3 py-1.5 bg-indigo-600/20 hover:bg-indigo-600/30 text-indigo-400 border border-indigo-600/30 rounded-lg text-sm transition-colors"
            >
              <Clock size={14} />
              Extend Trial
            </button>
            <button
              onClick={() => setRefundOpen(true)}
              className="flex items-center gap-2 px-3 py-1.5 bg-amber-600/20 hover:bg-amber-600/30 text-amber-400 border border-amber-600/30 rounded-lg text-sm transition-colors"
            >
              <CreditCard size={14} />
              Process Refund
            </button>
            <div className="p-2 bg-indigo-900/30 rounded-lg">
              <Shield className="h-5 w-5 text-indigo-400" />
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 bg-slate-800/50 rounded-lg p-1 w-fit">
        {(['roles', 'overrides', 'audit'] as DetailTab[]).map((t) => (
          <button
            key={t}
            onClick={() => setTab(t)}
            className={`px-4 py-2 rounded-lg text-sm font-medium capitalize transition-colors ${
              tab === t
                ? 'bg-slate-700 text-white'
                : 'text-slate-400 hover:text-white'
            }`}
          >
            {t}
            {t === 'overrides' && user.overrides.length > 0 && (
              <span className="ml-1.5 px-1.5 py-0.5 bg-amber-900/40 text-amber-400 rounded-full text-xs">
                {user.overrides.length}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Roles tab */}
      {tab === 'roles' && (
        <div className="space-y-4">
          <div className="flex justify-between items-center">
            <h3 className="text-white font-medium">Roles ({user.roles.length})</h3>
            <button
              onClick={() => setGrantRoleOpen(true)}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-indigo-600 hover:bg-indigo-500 text-white rounded-lg text-sm font-medium transition-colors"
            >
              <Plus className="h-4 w-4" />
              Grant Role
            </button>
          </div>

          {user.roles.length === 0 ? (
            <p className="text-slate-500 text-sm py-4">No roles assigned.</p>
          ) : (
            <div className="space-y-2">
              {user.roles.map((role: RbacRoleDetail) => (
                <div
                  key={role.name}
                  className="flex items-center justify-between p-3 bg-slate-800 border border-slate-700 rounded-lg"
                >
                  <div>
                    <p className="text-white font-medium text-sm">{role.name}</p>
                    <div className="flex items-center gap-3 mt-1 text-xs text-slate-400">
                      {role.is_system ? (
                        <span className="text-slate-500">system</span>
                      ) : (
                        <span>by {role.granted_by_email ?? 'unknown'}</span>
                      )}
                      <span>granted {formatDate(role.granted_at)}</span>
                      {role.expires_at && (
                        <span className="flex items-center gap-1 text-amber-400">
                          <Clock className="h-3 w-3" />
                          expires {formatDate(role.expires_at)}
                        </span>
                      )}
                    </div>
                  </div>
                  {!role.is_system && (
                    <button
                      onClick={() => handleRevokeRole(role.name)}
                      className="p-1.5 text-slate-500 hover:text-red-400 transition-colors rounded"
                      title="Revoke role"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Overrides tab */}
      {tab === 'overrides' && (
        <div className="space-y-4">
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-4">
              <h3 className="text-white font-medium">Permission Overrides</h3>
              <label className="flex items-center gap-2 text-xs text-slate-400 cursor-pointer">
                <input
                  type="checkbox"
                  checked={includeExpired}
                  onChange={(e) => setIncludeExpired(e.target.checked)}
                  className="rounded accent-indigo-500"
                />
                Show expired
              </label>
            </div>
            <button
              onClick={() => setAddOverrideOpen(true)}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-indigo-600 hover:bg-indigo-500 text-white rounded-lg text-sm font-medium transition-colors"
            >
              <Plus className="h-4 w-4" />
              Add Override
            </button>
          </div>

          {user.overrides.length === 0 ? (
            <p className="text-slate-500 text-sm py-4">No permission overrides.</p>
          ) : (
            <div className="space-y-2">
              {user.overrides.map((ov: RbacOverrideDetail) => (
                <div
                  key={ov.permission_key}
                  className={`flex items-center justify-between p-3 border rounded-lg ${
                    ov.is_expired
                      ? 'bg-slate-800/50 border-slate-700/50 opacity-60'
                      : 'bg-slate-800 border-slate-700'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    {ov.granted ? (
                      <CheckCircle2 className="h-4 w-4 text-green-400 flex-shrink-0" />
                    ) : (
                      <XCircle className="h-4 w-4 text-red-400 flex-shrink-0" />
                    )}
                    <div>
                      <p className="text-white font-medium text-sm">{ov.permission_key}</p>
                      <div className="flex items-center gap-3 mt-1 text-xs text-slate-400">
                        <span>{ov.reason}</span>
                        {ov.granted_by_email && <span>by {ov.granted_by_email}</span>}
                        {ov.expires_at && (
                          <span className={`flex items-center gap-1 ${ov.is_expired ? 'text-red-400' : 'text-amber-400'}`}>
                            <Clock className="h-3 w-3" />
                            {ov.is_expired ? 'expired' : 'expires'} {formatDate(ov.expires_at)}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  {!ov.is_expired && (
                    <button
                      onClick={() => handleRemoveOverride(ov.permission_key)}
                      className="p-1.5 text-slate-500 hover:text-red-400 transition-colors rounded"
                      title="Remove override"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Audit tab */}
      {tab === 'audit' && (
        <div className="space-y-4">
          <h3 className="text-white font-medium">Audit History</h3>
          {auditLoading ? (
            <div className="flex justify-center py-8">
              <Loader2 className="h-5 w-5 text-indigo-400 animate-spin" />
            </div>
          ) : auditEntries.length === 0 ? (
            <p className="text-slate-500 text-sm py-4">No audit events for this user.</p>
          ) : (
            <>
              <div className="space-y-1">
                {auditEntries.map((entry: RbacAuditLogEntry) => (
                  <div
                    key={entry.id}
                    className="flex items-start gap-3 p-3 bg-slate-800 border border-slate-700/50 rounded-lg text-sm"
                  >
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <span className="px-1.5 py-0.5 bg-indigo-900/40 text-indigo-300 rounded text-xs font-mono">
                          {entry.action}
                        </span>
                        <span className="text-slate-400 text-xs">{formatDate(entry.created_at)}</span>
                      </div>
                      <p className="text-slate-400 text-xs mt-1">
                        by {entry.actor_email ?? 'system'}
                      </p>
                      {Object.keys(entry.details).length > 0 && (
                        <pre className="mt-1 text-xs text-slate-500 font-mono whitespace-pre-wrap">
                          {JSON.stringify(entry.details, null, 2)}
                        </pre>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              {/* Audit pagination */}
              <div className="flex items-center justify-between text-sm text-slate-400 pt-1">
                <span>Total: {auditTotal}</span>
                <div className="flex gap-2">
                  <button
                    onClick={() => loadAudit(auditPage - 1)}
                    disabled={auditPage === 1}
                    className="px-3 py-1 rounded border border-slate-700 hover:bg-slate-700 disabled:opacity-40 transition-colors"
                  >
                    Prev
                  </button>
                  <span className="px-2 py-1">Page {auditPage}</span>
                  <button
                    onClick={() => loadAudit(auditPage + 1)}
                    disabled={auditPage * 20 >= auditTotal}
                    className="px-3 py-1 rounded border border-slate-700 hover:bg-slate-700 disabled:opacity-40 transition-colors"
                  >
                    Next
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}
